import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  tableCellClasses,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { theme } from "src/Theme/index";
import { useSelector } from "react-redux";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import { getUrl } from "src/Utils/Common";
import ColoredBox from "src/Components/Boxes/TopProductBox/ColoredBox";
import ProgressBar from "src/Components/ProgressBars/ProgressBar";
import DonutChart from "../StatisticsTest/components/DonutChartPinned";
import ReactMarkdown from "react-markdown";
import { ArrowBackIos } from "@mui/icons-material";
import TopSkus from "./TopSkus";
import HtmlRenderer from "src/Components/shared/shared/HtmlRenderer";

function TopSegmentBrand({
  hierarchy,
  updateBrand,
  onSkuClick,
  setValue,
  topSkuHier,
  setSummarized,
  setFull,
}) {
  const selectedFilter = useSelector((state) => state.filter.selectedFilter); // ✅ Get Redux filter

  const getApiFilters = () => {
    if (!selectedFilter || selectedFilter.length === 0) return {};

    return selectedFilter.reduce((acc, item) => {
      const { category, name } = item;

      if (!acc[category]) {
        acc[category] = []; // Initialize category array if not exists
      }

      acc[category].push(name); // Push the name value
      return acc;
    }, {});
  };

  // Redux: Get project ID
  const projectId = useSelector((state) => state.userProject.projectId);
  const currentChartType = useSelector((state) => state.chart.currentChartType);

  // Local State
  const [data, setData] = useState([]);
  const [storesData, setStoresData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [valueShare, setValueShare] = useState([]);
  const [volumeSales, setVolumeSales] = useState([]);
  const [donutChatOptions, setDonutChatOptions] = useState({});
  const [donutChatOptions2, setDonutChatOptions2] = useState({});
  const [summarizedInsightsBrand, setSummarizedInsightsBrand] = useState("");
  const [fullInsightsBrand, setFullInsightsBrand] = useState("");
  const [filter, setFilter] = useState({});

  // Styled Table Components
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      padding: "5px",
      color: theme.palette.primary.dark,
      fontSize: theme.typography.fontSize.xxsmall,
      textAlign: "center",
      justifyContent: "center",
    },
    [`&.${tableCellClasses.body}`]: {
      textTransform: "capitalize",
      color: theme.palette.primary.dark,
      fontSize: 10,
      textAlign: "center",
      padding: "3px",
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    height: 15,
  }));

  // Fetch API Data
  useEffect(() => {
    async function fetchTopProducts() {
      if (!projectId) {
        console.warn("Project ID is missing.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      // Extract hierarchy and hierarchy_value dynamically
      try {
        const filter = {}
        filter[hierarchy]=updateBrand;
        const url = `${getUrl()}/market_overview/top_products`;
        const token = localStorage.getItem("userId") || "";
        const headers = {
          "Content-Type": "application/json",
          "x-access-token": token,
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        };
        const payload = {
          project_id: projectId,
          hierarchy: topSkuHier,
          opps_filter: true,
          filters: filter,
          top_5: true,
          time_period: "P12M",
          target: null,
          token,
        };

        const response = await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();
        console.log(result)
        if (!result || Object.keys(result).length === 0) {
          throw new Error("Invalid API response structure.");
        }
        const stores_data = {};

        const names = result.index;

        for (let i = 0; i < names.length; i++) {
          const name = names[i];
          stores_data[name] = {};

          for (const key in result) {
            if (key !== "index") {
              stores_data[name][key] = result[key][i];
            }
          }
        }
        console.log(stores_data)
        setStoresData(stores_data);
        // Transform API response into a usable format
        const formattedData = result["index"].map((name, index) => ({
          name,
          volumeSalesY22: result["Volume_Sales_Previous"]?.[index] || 0,
          volumeSalesY23: result["Volume_Sales_Current"]?.[index] || 0,
          valueSalesY22: result["Value_Sales_Previous"]?.[index] || 0,
          valueSalesY23: result["Value_Sales_Current"]?.[index] || 0,
          valueShareY23: result["Value_Share_Current"]?.[index] || 0,
          dya: (result["DYA"]?.[index]/1_000_000).toFixed(2) || 0,
          iya: result["IYA"]?.[index] || 0,
          growthIdx: result["Growth_IDX"]?.[index] || 0,
          priceIdx: result["Price_Index_YA"]?.[index] || 0,
          impactOnSales: (result["Impact_on_Sales"]?.[index]/1_000_000) .toFixed(2)|| 0,
          fairValueShare: (result["Fair_Value_Sales"]?.[index]/1_000_000).toFixed(2) || 0,
          valueShareIndex: result["Value_Share_Index"]?.[index] || 0,
          volumeShareIndex: result["Volume_Share_Index"]?.[index] || 0

        }));
        setData(formattedData);
        setSummarized(result["insights_summary"]);
        setFull(result["insights_full"]);
      } catch (error) {
        console.error("Error fetching top products:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }

    fetchTopProducts();
  }, [projectId, currentChartType, selectedFilter, hierarchy]);

  const handleBrandSelection = (value) => {
    const filter = {}
    filter[hierarchy]=updateBrand;
    filter[topSkuHier]=value;

    setSelectedBrand(value);
    setFilter(filter);
    console.log(filter)
    console.log(storesData);
    console.log(value)
    setValueShare([
      {
        name: "valueSahre",
        data: [
          ((storesData["Total Market"]["Value_Sales_Current"] -
            storesData[value]["Value_Sales_Current"]) /
            storesData["Total Market"]["Value_Sales_Current"]) *
            100,
          (storesData[value]["Value_Sales_Current"] /
            storesData["Total Market"]["Value_Sales_Current"]) *
            100,
        ],
      },
    ]);
    setVolumeSales([
      {
        name: "volumeSales",
        data: [
          ((storesData["Total Market"]["Volume_Sales_Current"] -
            storesData[value]["Volume_Sales_Current"]) /
            storesData["Total Market"]["Volume_Sales_Current"]) *
            100,
          (storesData[value]["Volume_Sales_Current"] /
            storesData["Total Market"]["Volume_Sales_Current"]) *
            100,
        ],
      },
    ]);

    setDonutChatOptions({
      chart: {
        type: "donut",
      },
      colors: ["#08306B", "#21A0FF"],
      labels: [updateBrand, value],
      title: {
        text: "Value Sales 2024",
        align: "center",
        style: {
          fontSize: "11px",
          fontWeight: "bold",
        },
      },

      legend: {
        position: "bottom",
      },
      dataLabels: {
        enabled: true,
        fontSize: "13px",
        formatter: (val) => `${val.toFixed(1)}%`,
      },
      tooltip: {
        y: {
          formatter: (val) => `${val}`,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "30%", // Wider colored section (smaller hole)
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 300,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    });
    setDonutChatOptions2({
      chart: {
        type: "donut",
      },
      colors: ["#08306B", "#21A0FF"],
      labels: [updateBrand, value],
      title: {
        text: "Volume Sales 2023",
        align: "center",
        style: {
          fontSize: "11px",
          fontWeight: "bold",
        },
      },

      legend: {
        position: "bottom",
      },
      dataLabels: {
        enabled: true,
        fontSize: "13px",
        formatter: (val) => `${val.toFixed(1)}%`,
      },
      tooltip: {
        y: {
          formatter: (val) => `${val}`,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "30%", // Wider colored section (smaller hole)
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 300,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    });
  };

  return (
    <Paper>
      {loading ? (
        <FacebookCircularProgress />
      ) : error ? (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      ) : selectedBrand !== "" ? (
        <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          {/* Back Button */}
          <Grid item xs={12}>
            <IconButton
              onClick={() => {
                setSelectedBrand("");
                setValue([]);
              }}
            >
              <ArrowBackIos />
            </IconButton>
          </Grid>

          {/* Brand Name */}
          <Grid item xs={12}>
            <Typography
              fontWeight={theme.typography.fontWeightBold}
              textAlign="center"
              fontSize={{ xs: "20px", sm: "24px", md: "30px" }}
            >
              {selectedBrand}
            </Typography>
          </Grid>

          {/* Left Column: HTML Insights */}
          <Grid item xs={12} md={6}>
            {fullInsightsBrand === "" ? (
              <FacebookCircularProgress />
            ) :(
                  <HtmlRenderer htmlString={summarizedInsightsBrand} modalContent={fullInsightsBrand} />
            )
          }
          </Grid>

          {/* Right Column: Two Donut Charts */}
          <Grid item xs={12} md={6} container direction="column" spacing={2}>
            <Grid item>
              <Box sx={{ maxWidth: { xs: "100%", sm: 300 }, mx: "auto" }}>
                <DonutChart
                  title="Value Sales 2024"
                  dataValues={valueShare}
                  labels={[{ labels: ["Total Market", selectedBrand] }]}
                  chartOptions={donutChatOptions}
                />
              </Box>
            </Grid>

            <Grid item>
              <Box sx={{ maxWidth: { xs: "100%", sm: 300 }, mx: "auto" }}>
                <DonutChart
                  title="Volume Sales 2024"
                  dataValues={volumeSales}
                  labels={[{ labels: ["Total Market", selectedBrand] }]}
                  chartOptions={donutChatOptions2}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Bottom Section: Top SKUs */}
          <Grid item xs={12}>
            <TopSkus
              filter={filter}
              hier="sku_db"
              updatedBrand={selectedBrand}
              updateSku={typeof onSkuClick === "function" ? onSkuClick : () => {}}
              setSummarized={setSummarizedInsightsBrand}
              setFull={setFullInsightsBrand}
            />
          </Grid>
        </Grid>
      ) : (
        <>
          <TableContainer
            sx={{
              height: 200,
              overflowY: "auto",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              touchAction: "manipulation",
              backgroundColor: "#fff",
            }}
          >
            <Table stickyHeader aria-label="customized table">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f1f1f1" }}>
                  <StyledTableCell>#</StyledTableCell>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Value Share IYA</StyledTableCell>
                  <StyledTableCell>Volume Share IYA</StyledTableCell>
                  <StyledTableCell>Impact on Sales(M SAR)</StyledTableCell>
                  <StyledTableCell>Fair Value Sales(M SAR)</StyledTableCell>
                  <StyledTableCell>Growth IDX</StyledTableCell>
                  <StyledTableCell>Price IDX</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {data.map((item, idx) => {
                  const cellSx = idx === 0 ? { fontWeight: "bold" } : {};

                  return (
                    <StyledTableRow key={idx}>
                      <StyledTableCell sx={cellSx}>{idx + 1}</StyledTableCell>
                      <StyledTableCell sx={cellSx}>{item.name}</StyledTableCell>
                      <StyledTableCell sx={cellSx}>
                        <ColoredBox
                          number={item.valueShareIndex}
                          Color={
                            item.valueShareIndex > 0
                              ? "#0BB783"
                              : item.valueShareIndex === 0
                              ? "#0095FF"
                              : "#B22234"
                          }
                        />
                      </StyledTableCell>
                      <StyledTableCell sx={cellSx}>
                        <ColoredBox
                          number={item.volumeShareIndex}
                          Color={
                            item.volumeShareIndex > 0
                              ? "#0BB783"
                              : item.volumeShareIndex === 0
                              ? "#0095FF"
                              : "#B22234"
                          }
                        />
                      </StyledTableCell>
                      <StyledTableCell sx={cellSx}>
                        <ColoredBox
                          number={item.impactOnSales}
                          Color={
                            item.impactOnSales > 1
                              ? "#0BB783"
                              : item.impactOnSales === 0
                              ? "#0095FF"
                              : "#B22234"
                          }
                        />
                      </StyledTableCell>
                      <StyledTableCell sx={cellSx}>
                        <ColoredBox
                          number={item.fairValueShare}
                          Color={
                            item.fairValueShare > 1
                              ? "#0BB783"
                              : item.fairValueShare === 0
                              ? "#0095FF"
                              : "#B22234"
                          }
                        />
                      </StyledTableCell>
                      
                      <StyledTableCell sx={cellSx}>
                        <ColoredBox
                          number={item.growthIdx}
                          Color={
                            item.growthIdx > 1
                              ? "#0BB783"
                              : item.growthIdx === 1
                              ? "#0095FF"
                              : "#B22234"
                          }
                        />
                      </StyledTableCell>
                      <StyledTableCell sx={cellSx}>
                        <ColoredBox
                          number={item.priceIdx}
                          Color={
                            item.priceIdx > 1
                              ? "#0BB783"
                              : item.priceIdx === 1
                              ? "#0095FF"
                              : "#B22234"
                          }
                        />
                      </StyledTableCell>
                      <StyledTableCell sx={cellSx}>
                        {idx !== 0 && (
                          <span data-tour="diag-view-sku">
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleBrandSelection(item.name)}
                            >
                              View
                            </Button>
                          </span>
                        )}
                      </StyledTableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

        </>
      )}
    </Paper>
  );
}

export default TopSegmentBrand;
